import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import Pagination from 'components/v2/PaginationV2';
import { isEqual, isEmpty, uniqBy } from 'lodash';
import { humanize, unescapeHTML } from 'underscore.string';
import Spinner from 'components/ui/Spinner';

function getEventSource(source) {
  if (
    source.email === 'cargomatic-automation' ||
    source.email === 'cargomatic-automation@cargomatic.com'
  ) {
    return (
      <img
        src="/img/storm-trooper.jpg"
        alt="cargomatic-automation"
        className="icon"
        width="25"
      />
    );
  }

  if (source._type === 'Driver') {
    if (!source.first_name) return source.email;
    if (source.first_name && source.first_name !== '') {
      return `${source.username} (${source.first_name} ${source.last_name})`;
    }
    return source.username;
  }

  return source.email;
}
class AuditHistory extends Component {
  static propTypes = {
    audits: PropTypes.arrayOf(
      PropTypes.shape({
        meta: PropTypes.shape({
          value: PropTypes.any,
        }),
        event: PropTypes.string,
      }),
    ),
    paginate: PropTypes.bool,
    keepDuplicates: PropTypes.bool,
    itemsPerPage: PropTypes.number,
    onClick: PropTypes.func,
    isLoading: PropTypes.bool,
    currentPage: PropTypes.number,
  };

  static defaultProps = {
    paginate: true,
    itemsPerPage: 10,
  };

  constructor(props) {
    super(props);
    let { itemsPerPage, paginate, keepDuplicates, audits = [] } = props;

    let total = audits.length;
    if (!Array.isArray(audits)) {
      total = audits?.totalCount;
    }

    if (!keepDuplicates) {
      audits = uniqBy(audits, ({ meta, event }) =>
        JSON.stringify({ meta, event }),
      );
    }

    const pages = audits ? Math.ceil(audits.length / itemsPerPage) : 1;

    let activeItems = audits;
    if (paginate) {
      activeItems = audits.slice(0, itemsPerPage);
    }

    this.state = {
      audits,
      activeItems,
      activePage: 1,
      pages,
      itemsPerPage,
      total,
    };
  }

  onPageSelect = (page) => {
    const { audits, itemsPerPage, ...state } = this.state;
    let activePage = page;
    this.setState({
      ...state,
      activeItems: audits.slice(
        (activePage - 1) * itemsPerPage,
        activePage * itemsPerPage,
      ),
      activePage,
      itemsPerPage,
    });
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    let {
      audits: { items, totalCount },
      keepDuplicates,
    } = nextProps;

    if (!items) {
      return null;
    }

    const { activeItems, itemsPerPage, activePage } = prevState;

    if (!keepDuplicates) {
      items = uniqBy(items, ({ meta, event }) =>
        JSON.stringify({ meta, event }),
      );
    }

    if (!isEqual(activeItems, items)) {
      return {
        activeItems: items.slice(
          (activePage - 1) * itemsPerPage,
          activePage * itemsPerPage,
        ),
        total: totalCount,
      };
    }

    return null;
  }

  getDate = (date) => {
    return moment(date).format('L');
  };

  renderHistory() {
    if (!this.state.activeItems) {
      return null;
    }
    const { t, languageCodeId } = this.props;
    return this.state.activeItems.map((item, index) => {
      const event = humanize(item.event);
      let change = '';
      let shipment = '';
      let order = '';
      let link = '';
      let description = null;

      if (item.meta && item.meta.event_description) {
        description = item.meta.event_description;
      }

      if (item.meta && item.meta.value) {
        const value = item.meta.value;
        if (item.meta.shipment && typeof item.meta.shipment === 'string') {
          shipment = item.meta.shipment;
        }
        if (item.meta.order && typeof item.meta.order === 'string') {
          order = item.meta.order;
        }
        if (typeof value === 'string') {
          if (!isEmpty(shipment) || !isEmpty(order)) {
            const valueSplit = value.split(' ');
            const linkId = valueSplit[valueSplit.length - 1];
            valueSplit.pop();
            const path = !isEmpty(shipment) ? 'shipments' : 'orders';
            const id = !isEmpty(shipment) ? shipment : order;

            change = valueSplit.join(' ');
            link = (
              <a
                href={`/${path}/${id}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                {linkId}
              </a>
            );
          } else {
            change = unescapeHTML(unescape(item.meta.value));
          }
        }
      }
      let text;
      let title;
      if (change.length > 50) {
        text = change.substring(0, 40) + '...[truncated]';
        title = change;
      } else if (change && link) {
        text = `{change} {link}`;
        title = text;
      } else {
        text = change;
        title = change;
      }
      if (item?.meta?.contractId && item?.meta?.to) {
        const { start_date: toStartDate = '', end_date: toEndDate = '' } =
          item.meta.to;
        if (item?.meta?.from) {
          const { start_date: fromStartDate = '', end_date: fromEndDate = '' } =
            item.meta.from;
          change = t('START_DATE_END_DATE_CHANGED', {
            startFrom: this.getDate(fromStartDate),
            startTo: this.getDate(toStartDate),
            endFrom: this.getDate(fromEndDate),
            endTo: this.getDate(toEndDate),
          });
        }
        if (!change) {
          change = t('START_DATE_END_DATE_TIMELINE', {
            startDate: this.getDate(toStartDate),
            endDate: this.getDate(toEndDate),
          });
        }

        text = change;
      }

      const timestamp = moment(item.timestamp).format('LLL');
      const source = getEventSource(item.user);
      return (
        <tr key={index}>
          <td>
            <div className="w-48 text-xs break-all" title={event}>
              {event}
            </div>
            {!isEmpty(change) && (
              <div
                style={{ paddingTop: 20 }}
                className="w-48 text-xs break-all text-grey-dark"
                title={title}
              >
                {t('CHANGE_WITH_VALUE', { text })}
              </div>
            )}
            {description && (
              <div
                style={{ paddingTop: 20 }}
                className="w-48 text-xs break-all text-grey-dark"
                title={description}
              >
                {description}
              </div>
            )}
          </td>
          <td>
            <div className="w-20 text-xs" title={timestamp}>
              {timestamp}
            </div>
          </td>
          <td>
            <div className="w-24 text-xs" title={source}>
              {source}
            </div>
          </td>
        </tr>
      );
    });
  }

  renderPagination() {
    const { t, isLoading, onClick, currentPage } = this.props;
    const { activePage, itemsPerPage, total } = this.state;
    const pagination = currentPage || activePage;
    let onPageClick = onClick || this.onPageSelect;
    return (
      <div className="flex justify-center my-2">
        {isLoading ? (
          <Spinner message={t('LOADING')} />
        ) : (
          <Pagination
            perPage={itemsPerPage}
            page={pagination}
            total={total}
            onPageClick={onPageClick}
          />
        )}
      </div>
    );
  }

  render() {
    const { t } = this.props;
    if (
      !this.props.audits ||
      (this.state.activeItems && this.state.activeItems.length === 0)
    ) {
      return <div>{t('AWAITING_AUDIT_EVENTS')}..</div>;
    }

    return (
      <div className="overflow-x-auto">
        <table className="default-table">
          <thead>
            <tr>
              <th>{t('NAME')}</th>
              <th>{t('TIMESTAMP')}</th>
              <th>{t('SOURCE')}</th>
            </tr>
          </thead>
          <tbody>{this.renderHistory()}</tbody>
        </table>
        {this.props.paginate && this.renderPagination()}
      </div>
    );
  }
}
export default withTranslation()(AuditHistory);
